import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import SEOHead from '../components/SEOHead';
import { 
  Building, 
  Calendar, 
  CheckCircle, 
  Clock, 
  Filter,
  MapPin,
  Users,
  TrendingUp,
  Award,
  Search,
  Map
} from 'lucide-react';
import { projects, stats, serviceImages } from '../mock';
import { Link } from 'react-router-dom';

const ProjectsPage = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedYear, setSelectedYear] = useState('all');
  
  const categories = [
    { id: 'all', label: 'Tous les Projets' },
    { id: 'Audit QoS', label: 'Audit QoS' },
    { id: 'Contrôle Réseau', label: 'Contrôle Réseau' },
    { id: 'Digitalisation', label: 'Digitalisation' }
  ];
  
  const years = [
    { id: 'all', label: 'Toutes Années' },
    { id: '2026', label: '2026' },
    { id: '2025', label: '2025' },
    { id: '2024', label: '2024' },
    { id: '2023', label: '2023' },
    { id: '2022', label: '2022' },
    { id: '2021', label: '2021' },
    { id: '2020', label: '2020' }
  ];
  
  const filteredProjects = projects.filter(project => {
    const categoryMatch = selectedCategory === 'all' || project.category === selectedCategory;
    const yearMatch = selectedYear === 'all' || project.year === selectedYear;
    return categoryMatch && yearMatch;
  });
    
  const getStatusColor = (status) => {
    switch (status) {
      case 'En cours': return 'bg-blue-100 text-blue-800';
      case 'Terminé': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };
  
  const getStatusIcon = (status) => {
    switch (status) {
      case 'En cours': return <Clock className="w-4 h-4" />;
      case 'Terminé': return <CheckCircle className="w-4 h-4" />;
      default: return <Clock className="w-4 h-4" />;
    }
  };

  const projectStats = [
    {
      icon: TrendingUp,
      value: "13+",
      label: "Projets Réalisés",
      description: "Depuis 2020"
    },
    {
      icon: Users,
      value: "6+",
      label: "Clients Majeurs",
      description: "Secteur public & privé"
    },
    {
      icon: Map,
      value: stats.localites,
      label: "Localités Auditées",
      description: "Côte d'Ivoire"
    },
    {
      icon: Award,
      value: "100%",
      label: "Taux de Satisfaction",
      description: "Clients recommandent"
    }
  ];

  const siteUrl = "https://www.set-g.com";

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "@id": `${siteUrl}/projets#collection`,
    "name": "Projets et Réalisations SET-G - Audit QoS ARTCI, Orange CI",
    "description": "14+ projets réalisés en audit QoS, contrôle qualité réseau et digitalisation. Clients: ARTCI, ORANGE CI, SIR, MOOV AFRICA. +255 localités auditées.",
    "url": `${siteUrl}/projets`,
    "mainEntity": {
      "@type": "ItemList",
      "numberOfItems": projects.length,
      "itemListElement": projects.map((project, index) => ({
        "@type": "CreativeWork",
        "position": index + 1,
        "name": project.title,
        "description": project.description,
        "creator": {
          "@type": "Organization",
          "name": "SET-G"
        },
        "datePublished": project.year,
        "about": project.client
      }))
    }
  };

  return (
    <>
      <SEOHead
        title="Projets & Références | Audit QoS ARTCI, Orange CI, SIR"
        description="14+ projets réalisés par SET-G : Audit QoS pour ARTCI (+255 localités), PNCR Orange CI, digitalisation SIR. Découvrez nos références en télécommunications et IT en Côte d'Ivoire depuis 2020."
        keywords="projets audit QoS ARTCI, références SET-G, réalisations télécommunications Côte d'Ivoire, Orange CI PNCR, SIR digitalisation, MOOV AFRICA, contrôle qualité réseau mobile, benchmarking opérateurs"
        structuredData={structuredData}
      />

      <div className="min-h-screen bg-slate-50">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-slate-900 to-slate-800 text-white py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-4xl mx-auto">
              <div className="inline-flex items-center bg-green-100 text-green-800 px-4 py-2 rounded-full text-sm font-medium mb-6">
                <Award className="w-4 h-4 mr-2" />
                Nos Réalisations
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
                Projets & Références
              </h1>
              <p className="text-xl text-slate-300 leading-relaxed">
                Découvrez nos projets récents et nos partenariats avec les acteurs clés 
                du secteur des télécommunications et de l'IT en Afrique.
              </p>
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-4 gap-8">
              {projectStats.map((stat, index) => {
                const Icon = stat.icon;
                return (
                  <Card key={index} className="text-center hover:shadow-lg transition-shadow duration-300">
                    <CardContent className="pt-8">
                      <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <Icon className="w-8 h-8 text-orange-600" aria-hidden="true" />
                      </div>
                      <div className="text-3xl font-bold text-slate-900 mb-2">{stat.value}</div>
                      <h3 className="text-lg font-semibold text-slate-700 mb-1">{stat.label}</h3>
                      <p className="text-sm text-slate-500">{stat.description}</p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>

        {/* Technical Gallery */}
        <section className="py-16 bg-slate-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-slate-900 mb-4">
                Notre Expertise Technique
              </h2>
              <p className="text-lg text-slate-600 max-w-2xl mx-auto">
                Découvrez nos compétences à travers nos infrastructures et réalisations techniques
              </p>
            </div>
            
            <div className="grid md:grid-cols-3 gap-8">
              <Card className="overflow-hidden hover:shadow-xl transition-shadow duration-300">
                <div className="relative h-48">
                  <img 
                    src={serviceImages.radioEngineering}
                    alt="Infrastructure d'ingénierie radio 2G/3G/4G - Antennes et équipements de réseaux mobiles déployés par SET-G"
                    title="Ingénierie Radio 2G/3G/4G SET-G"
                    loading="lazy"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-blue-900/70 to-transparent"></div>
                  <div className="absolute bottom-4 left-4 text-white">
                    <h3 className="font-bold">Ingénierie Radio</h3>
                    <p className="text-sm opacity-90">Réseaux 2G/3G/4G</p>
                  </div>
                </div>
              </Card>
              
              <Card className="overflow-hidden hover:shadow-xl transition-shadow duration-300">
                <div className="relative h-48">
                  <img 
                    src={serviceImages.auditQos}
                    alt="Équipement d'audit et benchmarking QoS - Outils de mesure qualité de service réseau utilisés par SET-G pour ARTCI"
                    title="Audit QoS SET-G"
                    loading="lazy"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-orange-900/70 to-transparent"></div>
                  <div className="absolute bottom-4 left-4 text-white">
                    <h3 className="font-bold">Audit QoS</h3>
                    <p className="text-sm opacity-90">Benchmarking & Analyse</p>
                  </div>
                </div>
              </Card>
              
              <Card className="overflow-hidden hover:shadow-xl transition-shadow duration-300">
                <div className="relative h-48">
                  <img 
                    src={serviceImages.dataCenter}
                    alt="Data center et virtualisation - Infrastructure serveurs haute disponibilité déployée par SET-G"
                    title="Data Center SET-G"
                    loading="lazy"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-green-900/70 to-transparent"></div>
                  <div className="absolute bottom-4 left-4 text-white">
                    <h3 className="font-bold">Data Center</h3>
                    <p className="text-sm opacity-90">Virtualisation & Cloud</p>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </section>

        {/* Filters */}
        <section className="py-8 bg-slate-50 border-b">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-wrap items-center gap-6">
              <div className="flex items-center space-x-2">
                <Filter className="w-5 h-5 text-slate-500" />
                <span className="font-medium text-slate-700">Filtres:</span>
              </div>
              
              {/* Category Filter */}
              <div className="flex flex-wrap gap-2">
                {categories.map((category) => (
                  <Button
                    key={category.id}
                    variant={selectedCategory === category.id ? "default" : "outline"}
                    size="sm"
                    onClick={() => setSelectedCategory(category.id)}
                    className={`transition-all duration-200 ${
                      selectedCategory === category.id 
                        ? 'bg-orange-600 hover:bg-orange-700 text-white' 
                        : 'text-slate-700 hover:bg-slate-100'
                    }`}
                  >
                    {category.label}
                  </Button>
                ))}
              </div>
              
              {/* Year Filter */}
              <div className="flex flex-wrap gap-2">
                {years.map((year) => (
                  <Button
                    key={year.id}
                    variant={selectedYear === year.id ? "default" : "outline"}
                    size="sm"
                    onClick={() => setSelectedYear(year.id)}
                    className={`transition-all duration-200 ${
                      selectedYear === year.id 
                        ? 'bg-blue-600 hover:bg-blue-700 text-white' 
                        : 'text-slate-700 hover:bg-slate-100'
                    }`}
                  >
                    {year.label}
                  </Button>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Projects Grid */}
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {filteredProjects.length === 0 ? (
              <div className="text-center py-16">
                <Search className="w-16 h-16 text-slate-400 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-slate-700 mb-2">
                  Aucun projet trouvé
                </h3>
                <p className="text-slate-500">
                  Essayez de modifier vos filtres pour voir plus de projets.
                </p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {filteredProjects.map((project) => (
                  <Card key={project.id} className="group hover:shadow-xl transition-all duration-300 border border-slate-200 hover:border-orange-200">
                    <CardHeader className="pb-4">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center space-x-3">
                          <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                            <Building className="w-6 h-6 text-orange-600" />
                          </div>
                          <Badge 
                            className={`${getStatusColor(project.status)} flex items-center space-x-1`}
                          >
                            {getStatusIcon(project.status)}
                            <span>{project.status}</span>
                          </Badge>
                        </div>
                        <div className="text-right">
                          <div className="text-sm font-medium text-slate-900">{project.year}</div>
                          <div className="text-xs text-slate-500">{project.category}</div>
                        </div>
                      </div>
                      
                      <CardTitle className="text-xl font-bold text-slate-900 group-hover:text-orange-600 transition-colors leading-tight">
                        {project.title}
                      </CardTitle>
                      
                      <CardDescription className="flex items-center space-x-2 text-slate-600">
                        <Building className="w-4 h-4" />
                        <span className="font-medium">Client: {project.client}</span>
                      </CardDescription>
                    </CardHeader>
                    
                    <CardContent>
                      <p className="text-slate-600 leading-relaxed mb-4">
                        {project.description}
                      </p>
                      
                      {/* Project details */}
                      {(project.duration || project.impact) && (
                        <div className="space-y-2 mb-4 p-3 bg-slate-50 rounded-lg">
                          {project.duration && (
                            <div className="flex items-center justify-between text-sm">
                              <span className="text-slate-500">Durée:</span>
                              <span className="text-slate-700 font-medium">{project.duration}</span>
                            </div>
                          )}
                          {project.impact && (
                            <div className="flex items-center justify-between text-sm">
                              <span className="text-slate-500">Impact:</span>
                              <span className="text-green-600 font-medium">{project.impact}</span>
                            </div>
                          )}
                        </div>
                      )}
                      
                      <div className="flex items-center justify-between pt-4 border-t border-slate-100">
                        <div className="flex items-center space-x-2 text-sm text-slate-500">
                          <Calendar className="w-4 h-4" />
                          <span>Année {project.year}</span>
                        </div>
                        
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                          asChild
                        >
                          <Link to="/contact">
                            Plus d'infos
                          </Link>
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </section>
        
        {/* Testimonial */}
        <section className="py-20 bg-slate-900">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <div className="w-20 h-20 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-8">
                <Building className="w-10 h-10 text-orange-600" />
              </div>
              <blockquote className="text-2xl lg:text-3xl text-white font-medium leading-relaxed mb-8">
                "SET-G a démontré une expertise remarquable dans l'audit de nos réseaux mobiles. 
                Leur approche méthodique et leurs recommandations ont considérablement amélioré 
                la qualité de service pour nos utilisateurs."
              </blockquote>
              <div className="text-slate-300">
                <div className="font-semibold text-lg">Direction Technique</div>
                <div className="text-base">ARTCI - Autorité de Régulation des Télécommunications</div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-6">
              Votre Projet, Notre Expertise
            </h2>
            <p className="text-xl text-slate-600 mb-8">
              Découvrez comment nous pouvons transformer vos défis techniques en succès mesurables.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg" className="bg-orange-600 hover:bg-orange-700">
                <Link to="/contact">
                  Démarrer un Projet
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg">
                <Link to="/services">
                  Nos Services
                </Link>
              </Button>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default ProjectsPage;